import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams, useNavigate, useParams, Link } from "react-router-dom";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import { can } from "@/lib/permissions";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";
import ContractLinkChip from "@/components/ContractLinkChip";
import AnomalyBadge from "@/components/AnomalyBadge";
import { fmtDate } from "@/lib/dateUtils";
import { searchMatchTR } from "@/lib/textFormat";
import {
  FileXls, FilePdf, ImageSquare, FileText, FileArrowDown, Eye,
  MagnifyingGlass, Files, TrashSimple, X, CaretUp, CaretDown, CaretUpDown,
  Copy, Warning as WarningIcon, PencilSimple, ArrowLeft, MapPin,
} from "@phosphor-icons/react";

// Backend'in verdiği signed URL: /files/{path} — cookie ile auth olur.
const fileUrl = (path) => (path ? `${API}/files/${path}` : "");
// PDF mi resim mi ayır
const isPdf = (path) => (path || "").toLowerCase().endsWith(".pdf");

const SortIndicator = ({ active, dir }) => {
  if (!active) return <CaretUpDown size={10} className="inline ml-1 text-slate-400" />;
  return dir === "asc"
    ? <CaretUp size={10} weight="fill" className="inline ml-1 text-[#1B4776]" />
    : <CaretDown size={10} weight="fill" className="inline ml-1 text-[#1B4776]" />;
};

export default function DeliveryNotes() {
  const { user } = useAuth();
  const { activeSiteId, siteParams, sites, setActiveSiteId } = useSites();
  const { siteId: urlSiteId } = useParams();
  const isSiteScoped = !!urlSiteId;
  const currentSite = useMemo(
    () => (sites || []).find((s) => s.id === urlSiteId),
    [sites, urlSiteId],
  );
  const askConfirm = useConfirm();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [filter, setFilter] = useState({
    supplier_id: "all",
    date_from: "",
    date_to: "",
    q: "",             // irsaliye no / malzeme / firma serbest arama
    with_doc: "all",   // all | with | without
  });
  const [preview, setPreview] = useState(null); // { path, isPdf, meta }
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkLinkOpen, setBulkLinkOpen] = useState(false);
  const [bulkLinkContracts, setBulkLinkContracts] = useState([]);
  const [bulkLinkContractId, setBulkLinkContractId] = useState("");
  const [bulkLinkItemIndex, setBulkLinkItemIndex] = useState("0");
  const [bulkLinkSaving, setBulkLinkSaving] = useState(false);
  const [sort, setSort] = useState({ key: "delivery_date", dir: "desc" }); // varsayılan: en yeni önce
  // Yinelenen kayıt temizleme dialog state (yalnızca yönetici)
  const [dupOpen, setDupOpen] = useState(false);
  const [dupLoading, setDupLoading] = useState(false);
  const [dupData, setDupData] = useState(null);
  const [dupCleaning, setDupCleaning] = useState(false);
  const isAdmin = can.delete(user);

  const load = async () => {
    setLoading(true);
    try {
      // URL scoped ise siteId'yi override et; değilse SiteContext'i takip et
      const params = isSiteScoped ? { site_id: urlSiteId } : { ...siteParams() };
      if (filter.supplier_id && filter.supplier_id !== "all") params.supplier_id = filter.supplier_id;
      if (filter.date_from) params.date_from = filter.date_from;
      if (filter.date_to) params.date_to = filter.date_to;
      const { data } = await axios.get(`${API}/materials`, { params });
      setRows(data);
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { axios.get(`${API}/suppliers`).then(r => setSuppliers(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [activeSiteId, urlSiteId]);
  // Scoped görünümde SiteContext'i URL'e senkronla — üstteki genel seçici de aynı şantiyeyi yansıtsın
  useEffect(() => {
    if (isSiteScoped && activeSiteId !== urlSiteId) setActiveSiteId(urlSiteId);
  }, [isSiteScoped, urlSiteId]); // eslint-disable-line

  // ?duplicates=1 query param ile duplicate cleanup dialogunu otomatik aç
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("duplicates") === "1" && isAdmin && !dupOpen) {
      openDupDialog();
      // URL'i temizle
      const next = new URLSearchParams(searchParams);
      next.delete("duplicates");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAdmin]);

  // Client-side Türkçe-uyumlu (diakritik-duyarsız) arama + belge filtresi + sıralama
  const visible = useMemo(() => {
    const list = rows.filter(r => {
      if (filter.with_doc === "with" && !r.photo_path) return false;
      if (filter.with_doc === "without" && r.photo_path) return false;
      const hay = [
        r.delivery_note_no, r.material_name, r.supplier_name,
        r.recipient_supplier_name, r.site_name, r.created_by_name,
        r.carrier_name, r.carrier_plate, r.carrier_company_name,
      ].filter(Boolean).join(" ");
      return searchMatchTR(filter.q, hay);
    });

    const dir = sort.dir === "asc" ? 1 : -1;
    const getVal = (r) => {
      switch (sort.key) {
        case "delivery_date":       return r.delivery_date || "";
        case "delivery_note_no":    return (r.delivery_note_no || "").toLocaleLowerCase("tr");
        case "supplier_name":       return (r.supplier_name || "").toLocaleLowerCase("tr");
        case "material_name":       return (r.material_name || "").toLocaleLowerCase("tr");
        case "quantity":            return Number(r.quantity || 0);
        case "site_name":           return (r.site_name || "").toLocaleLowerCase("tr");
        case "carrier_name":        return (r.carrier_name || r.carrier_company_name || "").toLocaleLowerCase("tr");
        case "created_by_name":     return (r.created_by_name || "").toLocaleLowerCase("tr");
        default:                    return "";
      }
    };
    return [...list].sort((a, b) => {
      const va = getVal(a); const vb = getVal(b);
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * dir;
      return String(va).localeCompare(String(vb), "tr") * dir;
    });
  }, [rows, filter.q, filter.with_doc, sort]);

  const toggleSort = (key) => {
    setSort(prev => {
      if (prev.key !== key) {
        // İlk tıklama: sayısal ve tarih için desc, metin için asc
        const desc = key === "delivery_date" || key === "quantity";
        return { key, dir: desc ? "desc" : "asc" };
      }
      return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const stats = useMemo(() => {
    const withDoc = rows.filter(r => r.photo_path).length;
    return { total: rows.length, withDoc, withoutDoc: rows.length - withDoc };
  }, [rows]);

  const exportUrl = (kind) => {
    const p = new URLSearchParams(siteParams());
    if (filter.supplier_id && filter.supplier_id !== "all") p.set("supplier_id", filter.supplier_id);
    if (filter.date_from) p.set("date_from", filter.date_from);
    if (filter.date_to) p.set("date_to", filter.date_to);
    if (kind === "pdf") p.set("report_kind", "delivery");
    return `${API}/export/materials.${kind}?${p.toString()}`;
  };

  const openPreview = (r) => {
    if (!r.photo_path) {
      toast.info("Bu kayıtta belge bulunmuyor");
      return;
    }
    setPreview({ path: r.photo_path, isPdf: isPdf(r.photo_path), meta: r });
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedIds.size === visible.length && visible.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(visible.map(r => r.id)));
    }
  };
  const clearSelection = () => setSelectedIds(new Set());
  const bulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const ok = await askConfirm({
      title: "Toplu İrsaliye Silme",
      description: `${selectedIds.size} irsaliye kaydını silmek istiyor musunuz? 10 saniye içinde geri alabilirsiniz.`,
      destructive: true,
      confirmText: `Evet, ${selectedIds.size} Kaydı Sil`,
    });
    if (!ok) return;
    setBulkDeleting(true);
    try {
      const { data } = await axios.post(`${API}/materials/bulk-delete`, {
        ids: Array.from(selectedIds),
      });
      const deletedIds = data.ids || Array.from(selectedIds);
      await load();
      toast.success(`${data.deleted} irsaliye silindi`, {
        duration: 10000,
        action: {
          label: "Geri Al",
          onClick: async () => {
            try {
              const res = await axios.post(`${API}/materials/undo-delete`, { ids: deletedIds });
              toast.success(`${res.data.restored} irsaliye geri alındı`);
              await load();
            } catch (err) { toast.error(formatErr(err)); }
          },
        },
      });
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBulkDeleting(false);
    }
  };
  const allSelected = visible.length > 0 && selectedIds.size === visible.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < visible.length;

  // Toplu Sözleşme Eşleştirme — Feb 2026
  const selectedRows = visible.filter((r) => selectedIds.has(r.id));
  const selectedSiteIds = Array.from(new Set(selectedRows.map((r) => r.site_id).filter(Boolean)));
  const bulkLinkSiteMismatch = selectedSiteIds.length > 1;
  const bulkLinkSelectedContract = bulkLinkContracts.find((c) => c.id === bulkLinkContractId);
  const bulkLinkItems = bulkLinkSelectedContract?.items || [];

  const openBulkLink = async () => {
    if (selectedIds.size === 0) return;
    if (bulkLinkSiteMismatch) {
      toast.error("Seçilen irsaliyeler birden fazla şantiyeye ait. Toplu eşleştirme için tek şantiyeden kayıt seçin.");
      return;
    }
    setBulkLinkOpen(true);
    setBulkLinkContractId("");
    setBulkLinkItemIndex("0");
    try {
      const { data } = await axios.get(`${API}/contracts`, {
        params: { site_id: selectedSiteIds[0] },
      });
      setBulkLinkContracts(data || []);
    } catch (e) {
      toast.error("Sözleşmeler yüklenemedi");
      setBulkLinkContracts([]);
    }
  };

  const applyBulkLink = async () => {
    if (!bulkLinkContractId) { toast.error("Bir sözleşme seçin"); return; }
    const idx = bulkLinkItems.length > 1 ? parseInt(bulkLinkItemIndex, 10) : -1;
    setBulkLinkSaving(true);
    try {
      const { data } = await axios.post(`${API}/materials/bulk-contract-link`, {
        material_ids: Array.from(selectedIds),
        contract_id: bulkLinkContractId,
        item_index: idx,
      });
      const skipped = data.skipped_site_mismatch || 0;
      toast.success(
        `${data.updated} irsaliye ${data.contract_code || "sözleşmeye"} bağlandı.`
        + (skipped > 0 ? `  (${skipped} kayıt şantiye uyumsuzluğu nedeniyle atlandı)` : "")
      );
      setBulkLinkOpen(false);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBulkLinkSaving(false);
    }
  };

  // ---- Yinelenen kayıt temizleme (admin) ----
  const openDupDialog = async () => {
    setDupOpen(true);
    setDupData(null);
    setDupLoading(true);
    try {
      const { data } = await axios.get(`${API}/materials/duplicates`);
      setDupData(data);
    } catch (e) {
      toast.error(formatErr(e));
      setDupData({ group_count: 0, duplicate_extra_count: 0, groups: [] });
    } finally {
      setDupLoading(false);
    }
  };

  const runDupCleanup = async () => {
    if (!dupData || dupData.duplicate_extra_count === 0) return;
    const ok = await askConfirm({
      title: "Yinelenen Kayıtları Temizle",
      description: `Sistemde ${dupData.duplicate_extra_count} yinelenen irsaliye kaydı tespit edildi. Her gruptan EN ESKİ kayıt korunur, diğerleri silinir (geri alınabilir). Devam edilsin mi?`,
      destructive: true,
      confirmText: `Evet, ${dupData.duplicate_extra_count} Kaydı Sil`,
    });
    if (!ok) return;
    setDupCleaning(true);
    try {
      const { data } = await axios.post(`${API}/materials/duplicates/cleanup`, { dry_run: false });
      const deletedIds = data.ids || [];
      toast.success(`${data.deleted} yinelenen kayıt silindi`, {
        duration: 12000,
        action: {
          label: "Geri Al",
          onClick: async () => {
            try {
              const res = await axios.post(`${API}/materials/undo-delete`, { ids: deletedIds });
              toast.success(`${res.data.restored} kayıt geri alındı`);
              await load();
            } catch (err) { toast.error(formatErr(err)); }
          },
        },
      });
      setDupOpen(false);
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setDupCleaning(false);
    }
  };
  const clearBulkLink = async () => {
    if (selectedIds.size === 0) return;
    const ok = await askConfirm({
      title: "Bağlantıları Kaldır",
      description: `${selectedIds.size} irsaliyenin sözleşme bağlantıları kaldırılacak. Devam edilsin mi?`,
      confirmText: "Evet, Bağlantıları Kaldır",
    });
    if (!ok) return;
    setBulkLinkSaving(true);
    try {
      const { data } = await axios.post(`${API}/materials/bulk-contract-link`, {
        material_ids: Array.from(selectedIds),
        cleared: true,
      });
      toast.success(`${data.updated} irsaliyenin bağlantısı kaldırıldı`);
      setBulkLinkOpen(false);
      clearSelection();
      await load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBulkLinkSaving(false);
    }
  };

  return (
    <Shell
      title="İrsaliye Listesi"
      actions={
        <>
          {isAdmin && (
            <Button
              variant="outline"
              onClick={openDupDialog}
              data-testid="dn-duplicates-btn"
              className="rounded-none sm:rounded-sm border-amber-400 text-amber-800 hover:bg-amber-50"
            >
              <Copy size={16} className="mr-2" /> Yinelenenler
            </Button>
          )}
          <a href={exportUrl("xlsx")} data-testid="arch-export-xlsx">
            <Button variant="outline" className="rounded-none sm:rounded-sm border-slate-300">
              <FileXls size={16} className="mr-2" /> Excel
            </Button>
          </a>
          <a href={exportUrl("pdf")} data-testid="arch-export-pdf">
            <Button className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              <FilePdf size={16} className="mr-2" /> PDF
            </Button>
          </a>
        </>
      }
    >
      {/* Şantiye Scope Header (URL /irsaliye-arsivi/:siteId) */}
      {isSiteScoped && (
        <div className="flex items-center justify-between gap-4 p-3 mb-3 bg-blue-50/50 border border-[#1B4776]/40 rounded-none sm:rounded-sm"
             data-testid="dn-site-scope-header">
          <div className="flex items-center gap-3 min-w-0">
            <Link to="/irsaliye-arsivi"
                  className="p-1.5 rounded-full bg-white border border-[#1B4776]/40 text-[#1B4776] hover:bg-[#1B4776]/10 transition"
                  data-testid="dn-back-to-index">
              <ArrowLeft size={14} weight="bold" />
            </Link>
            <MapPin size={18} weight="duotone" className="text-[#1B4776] shrink-0" />
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#1B4776] font-bold">
                Aktif Şantiye
              </div>
              <div className="text-sm font-bold text-[#0F172A] truncate">
                <span className="font-mono">{currentSite?.code || urlSiteId}</span>
                {currentSite?.name && <span className="ml-2 text-slate-700">— {currentSite.name}</span>}
              </div>
            </div>
          </div>
          <Link to="/irsaliye-arsivi"
                className="text-xs text-[#1B4776] hover:underline shrink-0 font-semibold"
                data-testid="dn-change-site">
            Şantiye Değiştir
          </Link>
        </div>
      )}

      {/* KPI şeridi */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-500">Toplam Kayıt</div>
          <div className="text-2xl font-bold text-slate-900 font-heading mt-1" data-testid="arch-total">{stats.total}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-emerald-700">Belgeli</div>
          <div className="text-2xl font-bold text-emerald-700 font-heading mt-1" data-testid="arch-with">{stats.withDoc}</div>
        </Card>
        <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm">
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-700">Belgesiz</div>
          <div className="text-2xl font-bold text-amber-700 font-heading mt-1" data-testid="arch-without">{stats.withoutDoc}</div>
        </Card>
      </div>

      {/* Filtre şeridi */}
      <Card className="p-6 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="md:col-span-2">
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Ara</Label>
            <div className="relative mt-2">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                data-testid="arch-filter-q"
                value={filter.q}
                onChange={(e) => setFilter(f => ({ ...f, q: e.target.value }))}
                placeholder="İrsaliye no, malzeme, firma..."
                className="pl-9 rounded-none sm:rounded-sm"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Firma</Label>
            <Select value={filter.supplier_id} onValueChange={(v) => setFilter(f => ({ ...f, supplier_id: v }))}>
              <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="arch-filter-supplier">
                <SelectValue placeholder="Tümü" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Başlangıç</Label>
            <Input type="date" className="mt-2 rounded-none sm:rounded-sm" data-testid="arch-filter-from"
                   value={filter.date_from} onChange={(e) => setFilter(f => ({ ...f, date_from: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Bitiş</Label>
            <Input type="date" className="mt-2 rounded-none sm:rounded-sm" data-testid="arch-filter-to"
                   value={filter.date_to} onChange={(e) => setFilter(f => ({ ...f, date_to: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-slate-700">Belge Durumu</Label>
            <Select value={filter.with_doc} onValueChange={(v) => setFilter(f => ({ ...f, with_doc: v }))}>
              <SelectTrigger className="mt-2 rounded-none sm:rounded-sm" data-testid="arch-filter-doc">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tümü</SelectItem>
                <SelectItem value="with">Sadece Belgeliler</SelectItem>
                <SelectItem value="without">Sadece Belgesizler</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <Button onClick={load} data-testid="arch-apply"
                  className="rounded-none sm:rounded-sm bg-[#95C11F] hover:bg-[#7FA518] text-[#1B2A3A] font-bold">
            Uygula
          </Button>
        </div>
      </Card>

      {/* Tablo */}
      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 shadow-sm overflow-hidden" data-testid="arch-table">
        <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="text-sm text-slate-600">
            <span className="font-bold text-slate-900">{visible.length}</span> kayıt
          </div>
          <div className="text-xs text-slate-500">
            {loading ? "Yükleniyor..." : "Belgeler cookie-tabanlı yetki ile korunuyor."}
          </div>
        </div>

        {/* Toplu seçim bar */}
        {isAdmin && selectedIds.size > 0 && (
          <div
            data-testid="dn-bulk-selection-bar"
            className="flex flex-wrap items-center justify-between gap-3 px-6 py-3 border-b border-slate-200 bg-slate-50"
          >
            <div className="text-sm text-slate-900 font-medium">
              <span className="font-mono font-bold" data-testid="dn-bulk-selection-count">{selectedIds.size}</span> irsaliye seçildi
              {bulkLinkSiteMismatch && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold bg-amber-100 border border-amber-300 text-amber-800">
                  ⚠ Birden fazla şantiye
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Button
                variant="outline"
                onClick={clearSelection}
                data-testid="dn-bulk-clear-btn"
                className="h-8 rounded-none sm:rounded-sm border-slate-300 text-slate-700 hover:bg-slate-100 text-xs"
              >
                <X size={14} className="mr-1" /> Seçimi Temizle
              </Button>
              <Button
                onClick={openBulkLink}
                disabled={bulkLinkSaving || bulkLinkSiteMismatch}
                data-testid="dn-bulk-link-btn"
                className="h-8 rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white text-xs font-bold uppercase tracking-widest disabled:opacity-40"
              >
                <FileText size={14} className="mr-1.5" />
                Toplu Sözleşme Bağla ({selectedIds.size})
              </Button>
              <Button
                variant="outline"
                onClick={clearBulkLink}
                disabled={bulkLinkSaving}
                data-testid="dn-bulk-clear-link-btn"
                className="h-8 rounded-none sm:rounded-sm border-slate-400 text-slate-700 hover:bg-slate-100 text-xs"
              >
                Bağlantıları Kaldır
              </Button>
              <Button
                onClick={bulkDelete}
                disabled={bulkDeleting}
                data-testid="dn-bulk-delete-btn"
                className="h-8 rounded-none sm:rounded-sm bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-widest"
              >
                <TrashSimple size={14} className="mr-1.5" />
                {bulkDeleting ? "Siliniyor..." : `Sil (${selectedIds.size})`}
              </Button>
            </div>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-white">
                {isAdmin && (
                  <th className="w-10 px-4 py-3">
                    <Checkbox
                      checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                      onCheckedChange={toggleAll}
                      data-testid="dn-bulk-select-all"
                      aria-label="Tümünü seç"
                    />
                  </th>
                )}
                <th className="text-left px-4 py-3 font-bold w-24">Belge</th>
                <th className="text-left px-4 py-3 font-bold cursor-pointer select-none hover:bg-slate-50"
                    onClick={() => toggleSort("delivery_date")} data-testid="sort-delivery-date">
                  Tarih<SortIndicator active={sort.key === "delivery_date"} dir={sort.dir} />
                </th>
                <th className="text-left px-4 py-3 font-bold cursor-pointer select-none hover:bg-slate-50"
                    onClick={() => toggleSort("delivery_note_no")} data-testid="sort-note-no">
                  İrsaliye No<SortIndicator active={sort.key === "delivery_note_no"} dir={sort.dir} />
                </th>
                <th className="text-left px-4 py-3 font-bold cursor-pointer select-none hover:bg-slate-50"
                    onClick={() => toggleSort("supplier_name")} data-testid="sort-supplier">
                  Düzenleyen<SortIndicator active={sort.key === "supplier_name"} dir={sort.dir} />
                </th>
                <th className="text-left px-4 py-3 font-bold cursor-pointer select-none hover:bg-slate-50"
                    onClick={() => toggleSort("material_name")} data-testid="sort-material">
                  Malzeme<SortIndicator active={sort.key === "material_name"} dir={sort.dir} />
                </th>
                <th className="text-right px-4 py-3 font-bold cursor-pointer select-none hover:bg-slate-50"
                    onClick={() => toggleSort("quantity")} data-testid="sort-quantity">
                  Miktar<SortIndicator active={sort.key === "quantity"} dir={sort.dir} />
                </th>
                <th className="text-left px-4 py-3 font-bold cursor-pointer select-none hover:bg-slate-50"
                    onClick={() => toggleSort("site_name")} data-testid="sort-site">
                  Şantiye<SortIndicator active={sort.key === "site_name"} dir={sort.dir} />
                </th>
                <th className="text-left px-4 py-3 font-bold cursor-pointer select-none hover:bg-slate-50"
                    onClick={() => toggleSort("carrier_name")} data-testid="sort-carrier">
                  Taşıyıcı / Plaka<SortIndicator active={sort.key === "carrier_name"} dir={sort.dir} />
                </th>
                <th className="text-left px-4 py-3 font-bold cursor-pointer select-none hover:bg-slate-50"
                    onClick={() => toggleSort("created_by_name")} data-testid="sort-person">
                  Personel<SortIndicator active={sort.key === "created_by_name"} dir={sort.dir} />
                </th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r, i) => {
                const isChecked = selectedIds.has(r.id);
                return (
                <tr
                  key={r.id}
                  className={`${isChecked ? "bg-red-50" : (i % 2 ? "bg-slate-50" : "bg-white")} hover:bg-slate-100 transition-colors`}
                  data-testid={`arch-row-${i}`}
                >
                  {isAdmin && (
                    <td className="w-10 px-4 py-3">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={() => toggleOne(r.id)}
                        data-testid={`dn-bulk-select-${r.id}`}
                        aria-label={`Seç: ${r.delivery_note_no || r.material_name}`}
                      />
                    </td>
                  )}
                  <td className="px-4 py-3">
                    {r.photo_path ? (
                      <button
                        type="button"
                        onClick={() => openPreview(r)}
                        data-testid={`arch-thumb-${i}`}
                        className="w-14 h-14 border border-slate-300 hover:border-[#1B4776] bg-slate-100 flex items-center justify-center overflow-hidden group"
                        title="Belgeyi görüntüle"
                      >
                        {isPdf(r.photo_path) ? (
                          <FileText size={22} weight="duotone" className="text-[#1B4776]" />
                        ) : (
                          <img
                            src={fileUrl(r.photo_path)}
                            alt="Belge"
                            className="max-w-full max-h-full object-cover group-hover:scale-105 transition-transform"
                            onError={(e) => { e.currentTarget.style.display = "none"; }}
                          />
                        )}
                      </button>
                    ) : (
                      <div className="w-14 h-14 border border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                        <ImageSquare size={20} weight="duotone" />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{fmtDate(r.delivery_date) || "-"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.delivery_note_no || "-"}</td>
                  <td className="px-4 py-3 font-medium">{r.supplier_name || "-"}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span>{r.material_name}</span>
                        <AnomalyBadge anomaly={r.anomaly} testId={`arch-anomaly-${i}`} />
                      </div>
                      <ContractLinkChip
                        link={r.contract_link}
                        material={r}
                        onChange={(newLink) => {
                          setRows(prev => prev.map(x =>
                            x.id === r.id ? { ...x, contract_link: newLink } : x
                          ));
                        }}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono whitespace-nowrap">
                    {r.quantity} <span className="text-slate-500 text-xs">{r.unit}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-700 text-xs">{r.site_name || "-"}</td>
                  <td className="px-4 py-3 text-xs">
                    {(r.carrier_name || r.carrier_plate || r.carrier_company_name) ? (
                      <div className="flex flex-col gap-0.5 min-w-[140px]">
                        {(r.carrier_name || r.carrier_company_name) && (
                          <span className="text-slate-800 font-medium truncate max-w-[180px]"
                                title={r.carrier_name || r.carrier_company_name}>
                            {r.carrier_name || r.carrier_company_name}
                          </span>
                        )}
                        {r.carrier_plate && (
                          <span className="inline-flex items-center gap-1 self-start px-1.5 py-0 border border-slate-800 bg-yellow-300 font-mono text-[10px] font-bold text-slate-900 tracking-wider">
                            {r.carrier_plate}
                          </span>
                        )}
                        {r.carrier_company_phone && (
                          <a href={`tel:${r.carrier_company_phone}`}
                             className="text-[10px] font-mono text-[#556B10] hover:underline"
                             onClick={(e) => e.stopPropagation()}>
                            ☎ {r.carrier_company_phone}
                          </a>
                        )}
                      </div>
                    ) : (
                      <span className="text-slate-400 text-[11px]">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{r.created_by_name || "-"}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center gap-2 justify-end">
                      {can.writeMaterial(user) && (
                        <button
                          type="button"
                          onClick={() => navigate(`/malzeme-girisi?edit=${r.id}`)}
                          data-testid={`arch-edit-${i}`}
                          title="İrsaliyeyi düzenle"
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#1B4776] hover:underline"
                        >
                          <PencilSimple size={12} weight="bold" /> Düzenle
                        </button>
                      )}
                      {r.photo_path && (
                        <a
                          href={fileUrl(r.photo_path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`arch-open-${i}`}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#1B4776] hover:underline"
                        >
                          <FileArrowDown size={12} weight="bold" /> İndir
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
                );
              })}
              {visible.length === 0 && !loading && (
                <tr>
                  <td colSpan={isAdmin ? 10 : 9} className="py-16 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <Files size={32} weight="duotone" className="text-slate-400" />
                      <div>Kayıt bulunamadı.</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Preview modal */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="max-w-5xl rounded-none sm:rounded-sm p-0">
          <DialogHeader className="p-4 border-b border-slate-200 bg-slate-50">
            <DialogTitle className="text-base font-heading font-bold text-slate-900 flex items-center gap-2">
              <Eye size={18} weight="duotone" className="text-[#1B4776]" />
              İrsaliye Belgesi
              {preview?.meta?.delivery_note_no && (
                <span className="font-mono text-xs text-slate-500 ml-2">
                  #{preview.meta.delivery_note_no}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="p-4 max-h-[75vh] overflow-auto bg-slate-100 flex items-center justify-center">
            {preview?.isPdf ? (
              <iframe
                title="Belge"
                src={fileUrl(preview.path)}
                className="w-full h-[70vh] border border-slate-300 bg-white"
              />
            ) : preview?.path ? (
              <img
                src={fileUrl(preview.path)}
                alt="Belge"
                className="max-w-full max-h-[70vh] object-contain bg-white shadow-sm"
              />
            ) : null}
          </div>
          {preview?.meta && (
            <div className="p-4 border-t border-slate-200 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs bg-white">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Tarih</div>
                <div className="font-mono">{fmtDate(preview.meta.delivery_date)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Düzenleyen</div>
                <div className="font-medium">{preview.meta.supplier_name || "-"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Düzenlenen</div>
                <div className="font-medium">{preview.meta.recipient_supplier_name || "-"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Şantiye</div>
                <div>{preview.meta.site_name || "-"}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Malzeme</div>
                <div>{preview.meta.material_name} — <span className="font-mono">{preview.meta.quantity} {preview.meta.unit}</span></div>
              </div>
              <div className="col-span-2">
                <a href={fileUrl(preview.path)} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1 text-xs font-bold text-[#1B4776] hover:underline">
                  <FileArrowDown size={13} weight="bold" /> Belgeyi indir / yeni sekmede aç
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Toplu Sözleşme Eşleştirme Dialog — Feb 2026 */}
      <Dialog open={bulkLinkOpen} onOpenChange={setBulkLinkOpen}>
        <DialogContent className="max-w-lg rounded-none sm:rounded-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} weight="duotone" className="text-[#1B4776]" />
              Toplu Sözleşme Eşleştirme
            </DialogTitle>
            <div className="text-xs text-slate-600 mt-1">
              Seçili <b>{selectedIds.size}</b> irsaliye kaydını tek seferde bir sözleşmeye bağlayın.
              Tedarikçi farkı olsa da bağlantı kurulabilir; sadece <b>şantiye</b> aynı olmalıdır.
            </div>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-700">Sözleşme</Label>
              <Select value={bulkLinkContractId || undefined}
                      onValueChange={(v) => { setBulkLinkContractId(v); setBulkLinkItemIndex("0"); }}>
                <SelectTrigger data-testid="dn-bulk-link-contract" className="mt-1 rounded-none sm:rounded-sm">
                  <SelectValue placeholder="Sözleşme seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {bulkLinkContracts.length === 0 && (
                    <div className="px-3 py-2 text-xs text-slate-500 text-center">
                      Bu şantiye için sözleşme bulunamadı.
                    </div>
                  )}
                  {bulkLinkContracts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-[#1B4776]">{c.code || "(kodsuz)"}</span>
                        <span className="text-xs text-slate-600 truncate max-w-[240px]">
                          {c.material_name || `${(c.items || []).length} kalem`}
                        </span>
                        {c.supplier_name && (
                          <span className="text-[9px] uppercase tracking-widest px-1 py-0 border border-slate-300 text-slate-500 bg-slate-50">
                            {c.supplier_name}
                          </span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {bulkLinkItems.length > 1 && (
              <div>
                <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-700">Sözleşme Kalemi</Label>
                <Select value={bulkLinkItemIndex} onValueChange={setBulkLinkItemIndex}>
                  <SelectTrigger data-testid="dn-bulk-link-item" className="mt-1 rounded-none sm:rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {bulkLinkItems.map((it, i) => (
                      <SelectItem key={i} value={String(i)}>
                        <span className="text-xs">
                          <span className="font-mono text-slate-500 mr-2">#{i + 1}</span>
                          {it.material_name}
                          {it.unit && <span className="text-slate-400 ml-1">({it.quantity} {it.unit})</span>}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {bulkLinkSelectedContract && (
              <div className="p-3 bg-slate-50 border-l-2 border-[#95C11F] text-xs text-slate-700">
                <div>
                  <b>{bulkLinkSelectedContract.code}</b> — {bulkLinkSelectedContract.supplier_name}
                </div>
                <div className="text-slate-500 mt-0.5">
                  Toplam: {(bulkLinkSelectedContract.total_qty || 0).toLocaleString("tr-TR")}
                  {" · "}Kalan: <b>{(bulkLinkSelectedContract.remaining_qty || 0).toLocaleString("tr-TR")}</b>
                  {" · "}%{(bulkLinkSelectedContract.progress_percent || 0).toFixed(1)}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setBulkLinkOpen(false)}
                    data-testid="dn-bulk-link-cancel"
                    className="rounded-none sm:rounded-sm">
              Vazgeç
            </Button>
            <Button onClick={applyBulkLink}
                    disabled={!bulkLinkContractId || bulkLinkSaving}
                    data-testid="dn-bulk-link-apply"
                    className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
              {bulkLinkSaving ? "Bağlanıyor..." : `${selectedIds.size} İrsaliyeyi Bağla`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Yinelenen kayıt temizleme dialogu (admin) */}
      <Dialog open={dupOpen} onOpenChange={setDupOpen}>
        <DialogContent
          data-testid="dn-duplicates-dialog"
          className="max-w-2xl rounded-none sm:rounded-sm"
        >
          <DialogHeader>
            <DialogTitle className="font-heading font-bold flex items-center gap-2">
              <Copy size={18} weight="duotone" className="text-amber-700" />
              Yinelenen İrsaliye Kayıtlarını Temizle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 mt-2">
            {dupLoading && (
              <div className="text-sm text-slate-500 py-6 text-center">Kayıtlar taranıyor...</div>
            )}
            {!dupLoading && dupData && dupData.duplicate_extra_count === 0 && (
              <div className="py-6 text-center text-emerald-700 text-sm font-medium" data-testid="dn-dup-empty">
                Yinelenen kayıt bulunamadı. Sistem temiz.
              </div>
            )}
            {!dupLoading && dupData && dupData.duplicate_extra_count > 0 && (
              <>
                <div className="flex items-start gap-2 p-3 bg-amber-50 border-l-2 border-amber-500 text-xs text-amber-900">
                  <WarningIcon size={16} weight="fill" className="flex-shrink-0 mt-0.5 text-amber-700" />
                  <div>
                    <div className="font-bold mb-1">
                      {dupData.group_count} farklı grupta toplam <span data-testid="dn-dup-total">{dupData.duplicate_extra_count}</span> yinelenen kayıt bulundu.
                    </div>
                    <div>
                      Her grupta EN ESKİ kayıt korunur, aynı içeriğe sahip sonraki kayıtlar silinir.
                      Yanlışlıkla silinirse "Geri Al" ile 10 saniye içinde geri alabilirsiniz.
                    </div>
                  </div>
                </div>

                <div className="max-h-80 overflow-y-auto border border-slate-200">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr className="text-[10px] uppercase tracking-widest text-slate-600">
                        <th className="text-left px-3 py-2 font-bold">Malzeme</th>
                        <th className="text-left px-3 py-2 font-bold">İrsaliye No / Tarih</th>
                        <th className="text-left px-3 py-2 font-bold">Firma / Şantiye</th>
                        <th className="text-right px-3 py-2 font-bold w-24">Fazla</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dupData.groups || []).slice(0, 200).map((g, i) => (
                        <tr key={i} className={i % 2 ? "bg-white" : "bg-slate-50/50"}
                            data-testid={`dn-dup-row-${i}`}>
                          <td className="px-3 py-2 align-top">
                            <div className="font-medium">{g.signature.material_name}</div>
                            <div className="text-slate-500">
                              {g.signature.quantity} {g.signature.unit}
                            </div>
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div className="font-mono text-slate-800">{g.signature.delivery_note_no || "-"}</div>
                            <div className="text-slate-500">{fmtDate(g.signature.delivery_date)}</div>
                          </td>
                          <td className="px-3 py-2 align-top">
                            <div>{g.signature.supplier_name || "-"}</div>
                            <div className="text-slate-500">{g.signature.site_name || "-"}</div>
                          </td>
                          <td className="px-3 py-2 text-right align-top">
                            <span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 font-bold font-mono">
                              +{g.duplicates.length}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {(dupData.groups || []).length > 200 && (
                    <div className="px-3 py-2 text-[10px] text-slate-500 border-t border-slate-200 bg-slate-50">
                      İlk 200 grup gösteriliyor. Temizleme işlemi tüm grupları kapsar.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDupOpen(false)}
                    data-testid="dn-dup-cancel"
                    className="rounded-none sm:rounded-sm">
              Kapat
            </Button>
            {dupData && dupData.duplicate_extra_count > 0 && (
              <Button
                onClick={runDupCleanup}
                disabled={dupCleaning}
                data-testid="dn-dup-cleanup"
                className="rounded-none sm:rounded-sm bg-red-600 hover:bg-red-700 text-white font-bold uppercase tracking-widest"
              >
                <TrashSimple size={14} className="mr-2" />
                {dupCleaning ? "Temizleniyor..." : `${dupData.duplicate_extra_count} Fazla Kaydı Sil`}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
